/**
 * @fileoverview HTTP client helpers for Vote Forward API requests,
 * including optional and authenticated token flows.
 *
 * @module lib/apiClient
 */

import axios from 'axios';

import isInvalidLoginError from './functions/isInvalidLoginError';
import isNonEmptyString from './functions/isNonEmptyString';

const apiUrl = process.env.REACT_APP_API_URL || '';

/**
 * Acquires a token before an authenticated request can be sent.
 *
 * @param {Object} auth - Auth context object.
 * @returns {Promise<string>} Nonempty access token.
 * @throws {Error} If the provider resolves without a token.
 */
async function requireAccessToken(auth) {
  const accessToken = await auth.getAccessToken();
  if (!isNonEmptyString(accessToken)) {
    throw new Error('An access token is required for authenticated requests');
  }
  return accessToken;
}

/**
 * Sends an authenticated request, ending the session if its token has no matching login record.
 *
 * @param {Object} auth - Auth context object with an endInvalidLoginSession method.
 * @param {Function} request - Sends the request and resolves to its response data.
 * @returns {Promise<*>} Response data from the API
 * @throws {Error} The request's error. For an invalid login it is rethrown once
 *   `auth.endInvalidLoginSession` settles, which is at once if a session ending is already in progress.
 */
async function sendAuthenticated(auth, request) {
  try {
    return await request();
  } catch (err) {
    if (isInvalidLoginError(err)) {
      await auth.endInvalidLoginSession(err.response.data);
    }
    throw err;
  }
}

/**
 * Makes a GET request to the API without authentication.
 *
 * @param {string} path - API endpoint path (e.g., '/v1/campaigns')
 * @param {Object} [options={}] - Axios request options
 * @returns {Promise<*>} Response data from the API
 */
async function get(path, options = {}) {
  const { data } = await axios.get(`${ apiUrl }${ path }`, options);
  return data;
}

/**
 * Makes a GET request, using authentication if auth object is provided.
 *
 * @param {string} path - API endpoint path
 * @param {Object|null} auth - Auth context object with getAccessToken and endInvalidLoginSession methods, or null
 * @param {Object} [options={}] - Axios request options
 * @returns {Promise<*>} Response data from the API
 */
async function getWithOptionalToken(path, auth, options = {}) {
  if (auth) {
    return getWithToken(path, auth, options);
  }
  return await get(path, options);
}

/**
 * Makes an authenticated GET request to the API.
 *
 * @param {string} path - API endpoint path
 * @param {Object} auth - Auth context object with getAccessToken and endInvalidLoginSession methods
 * @param {Object} [options={}] - Axios request options
 * @returns {Promise<*>} Response data from the API
 */
async function getWithToken(path, auth, options = {}) {
  const accessToken = await requireAccessToken(auth);
  return await sendAuthenticated(auth, () => get(path, {
    ...options,
    headers: {
      ...options?.headers,
      Authorization: `Bearer ${ accessToken }`,
    },
  }));
}

/**
 * Makes a POST request to the API without authentication.
 *
 * @param {string} path - API endpoint path
 * @param {*} postData - Request body data
 * @param {Object} [options={}] - Axios request options
 * @returns {Promise<*>} Response data from the API
 */
async function post(path, postData, options = {}) {
  const { data } = await axios.post(`${ apiUrl }${ path }`, postData, options);
  return data;
}

/**
 * Makes an authenticated POST request to the API.
 *
 * @param {string} path - API endpoint path
 * @param {*} postData - Request body data
 * @param {Object} auth - Auth context object with getAccessToken and endInvalidLoginSession methods
 * @param {Object} [options={}] - Axios request options
 * @returns {Promise<*>} Response data from the API
 */
async function postWithToken(path, postData, auth, options = {}) {
  const accessToken = await requireAccessToken(auth);
  return await sendAuthenticated(auth, () => post(path,
    postData,
    {
      ...options,
      headers: {
        ...options?.headers,
        Authorization: `Bearer ${ accessToken }`,
      },
    }));
}

/**
 * Makes a PATCH request to the API without authentication.
 *
 * @param {string} path - API endpoint path
 * @param {*} postData - Request body data (partial update)
 * @param {Object} [options={}] - Axios request options
 * @returns {Promise<*>} Response data from the API
 */
async function patch(path, postData, options = {}) {
  const { data } = await axios.patch(`${ apiUrl }${ path }`, postData, options);
  return data;
}

/**
 * Makes an authenticated PATCH request to the API.
 *
 * @param {string} path - API endpoint path
 * @param {*} postData - Request body data (partial update)
 * @param {Object} auth - Auth context object with getAccessToken and endInvalidLoginSession methods
 * @param {Object} [options={}] - Axios request options
 * @returns {Promise<*>} Response data from the API
 */
async function patchWithToken(path, postData, auth, options = {}) {
  const accessToken = await requireAccessToken(auth);
  return await sendAuthenticated(auth, () => patch(path,
    postData,
    {
      ...options,
      headers: {
        ...options?.headers,
        Authorization: `Bearer ${ accessToken }`,
      },
    }));
}

export {
  get,
  getWithOptionalToken,
  getWithToken,
  patch,
  patchWithToken,
  post,
  postWithToken,
};
